import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  colors,
  HPI_I_VALIDATION_PATTERN,
} from '@scriptpad/shared';
import { useMutation } from '@tanstack/react-query';

import { submitHpiI } from '../api/client.js';
import { StepPaper } from './StepPaper.js';
import type { WizardStepProps } from './WizardRouter.js';

const formatHpiI = (raw: string): string => raw.replace(/\D/g, '').slice(0, 16);

export const Step5HpiI = ({ guid, data, onAdvance, onPatched }: WizardStepProps) => {
  const initial = data.hpiIRecord?.hpiI ?? '';
  const [hpiI, setHpiI] = useState(initial);
  const [touched, setTouched] = useState(false);

  const mutation = useMutation({
    mutationFn: () => submitHpiI(guid, hpiI),
    onSuccess: (next) => {
      onPatched(next);
      onAdvance(6);
    },
  });

  const isValid = HPI_I_VALIDATION_PATTERN.test(hpiI);
  const showError = touched && !isValid && hpiI.length > 0;
  const orgBackStep = data.practiceMode === 'sole_practitioner' ? 3 : 4;

  return (
    <StepPaper>
      <Stack spacing={3}>
        <Typography variant="h3">Verify your HPI-I</Typography>

        <Typography variant="body1" color="text.secondary">
          First, we need to validate your HPI-I (Healthcare Provider Identifier - Individual) to verify
          your identity as a healthcare professional.
        </Typography>

        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <strong>1.</strong> To retrieve your HPI-I, log in to PRODA and click <strong>Link
            identifiers</strong> under <strong>HPOS</strong>:
          </Typography>
          <Box
            component="img"
            src="/proda-link-identifiers.png"
            alt="PRODA HPOS Link identifiers button"
            sx={{
              display: 'block',
              maxWidth: 320,
              width: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
              bgcolor: colors.bgWash,
            }}
          />
        </Box>

        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <strong>2.</strong> Enter your AHPRA registration number and click <strong>Search</strong>:
          </Typography>
          <Box
            component="img"
            src="/proda-ahpra-search.png"
            alt="PRODA AHPRA registration number search field"
            sx={{
              display: 'block',
              maxWidth: 480,
              width: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
              bgcolor: colors.bgWash,
            }}
          />
        </Box>

        <Typography variant="body1" color="text.secondary">
          If the AHPRA number matches your PRODA details, you'll be shown your HPI-I number.
        </Typography>

        <TextField
          label="Your HPI-I"
          value={hpiI}
          onChange={(e) => setHpiI(formatHpiI(e.target.value))}
          onBlur={() => setTouched(true)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="800361..."
          error={showError || mutation.isError}
          helperText={
            showError
              ? 'Your HPI-I must be 16 digits beginning with 800361.'
              : 'Format: 16 digits beginning with 800361.'
          }
        />

        {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Button onClick={() => onAdvance(orgBackStep)} variant="text">
            Back
          </Button>
          <Button
            size="large"
            variant="contained"
            disabled={!isValid || mutation.isPending}
            onClick={() => {
              setTouched(true);
              if (isValid) mutation.mutate();
            }}
            startIcon={mutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {mutation.isPending ? 'Validating...' : 'Validate'}
          </Button>
        </Stack>
      </Stack>
    </StepPaper>
  );
};
